import {
  Bell,
  BookOpen,
  HelpCircle,
  GraduationCap,
  Headphones,
  Home,
  Menu,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  Phone,
  PlusCircle,
  ShieldCheck,
  SunMoon,
  User,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';

interface NavDef {
  to: string;
  label: string;
  icon: typeof Home;
  matchPrefix?: string;
}

const navItems: NavDef[] = [
  { to: '/dashboard', label: 'Главная', icon: Home },
  { to: '/tickets', label: 'Мои обращения', icon: MessageSquare, matchPrefix: '/tickets' },
  { to: '/tickets/new', label: 'Создать обращение', icon: PlusCircle },
  { to: '/knowledge', label: 'База знаний', icon: BookOpen, matchPrefix: '/knowledge' },
  { to: '/faq', label: 'FAQ', icon: HelpCircle },
  { to: '/training', label: 'Обучение', icon: GraduationCap },
  { to: '/passport', label: 'Паспорт клуба', icon: ShieldCheck },
  { to: '/my-data', label: 'Мои данные', icon: User },
  { to: '/contacts', label: 'Контакты', icon: Phone },
];

const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'Главная',
  '/tickets': 'Мои обращения',
  '/tickets/new': 'Создать обращение',
  '/knowledge': 'База знаний',
  '/faq': 'FAQ',
  '/training': 'Обучение',
  '/passport': 'Паспорт клуба',
  '/my-data': 'Мои данные',
  '/contacts': 'Контакты',
};

const COLLAPSED_KEY = 'recore-sidebar-collapsed';

function pageTitleFor(pathname: string): string {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  for (const [prefix, title] of Object.entries(PAGE_TITLES)) {
    if (prefix !== '/' && pathname.startsWith(prefix)) return title;
  }
  return '';
}

function initials(user: { firstName?: string; lastName?: string; email?: string } | null): string {
  if (!user) return '?';
  const fn = (user.firstName || '').trim();
  const ln = (user.lastName || '').trim();
  if (fn || ln) return `${fn[0] ?? ''}${ln[0] ?? ''}`.toUpperCase() || '?';
  const em = (user.email || '').trim();
  return em ? em.slice(0, 2).toUpperCase() : '?';
}

function displayName(user: { firstName?: string; lastName?: string; email?: string } | null): string {
  if (!user) return 'Гость';
  const fn = (user.firstName || '').trim();
  const ln = (user.lastName || '').trim();
  if (fn && ln) return `${fn} ${ln[0]}.`;
  if (fn) return fn;
  return user.email || 'Гость';
}

/**
 * Design-transfer shell (Club:Core prototype). Оборачивает всё поверх
 * .dc-scope — новые стили из design/styles.dc.css применяются только
 * внутри этого класса, не задевая старую разметку (tailwind + index.css).
 */
export default function AppShell({ children }: { children?: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const themeMode = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    document.documentElement.dataset.theme = themeMode === 'light' ? 'light' : 'dark';
  }, [themeMode]);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const activePath = location.pathname;
  const isActive = (item: NavDef) => {
    if (item.to === '/tickets') {
      return activePath !== '/tickets/new' && (activePath === '/tickets' || activePath.startsWith('/tickets/'));
    }

    if (item.to === '/tickets/new') {
      return activePath === '/tickets/new';
    }

    if (item.matchPrefix) {
      return activePath === item.matchPrefix || activePath.startsWith(item.matchPrefix + '/');
    }

    return activePath === item.to;
  };

  return (
    <div className="dc">
      <div className={`app ${collapsed ? 'nav-collapsed' : ''}`}>
        <aside className={`sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
          <div className="side-brand">
            <div className="brand-mark">
              <Headphones size={20} />
            </div>
            <span className="brand-name">Club:Core</span>
            <button
              className="icon-btn collapse-btn"
              type="button"
              onClick={() => setCollapsed((v) => !v)}
              aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
            >
              {collapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
            </button>
          </div>
          <nav className="side-nav" aria-label="Разделы портала">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`nav-item ${isActive(item) ? 'active' : ''}`}
                  aria-current={isActive(item) ? 'page' : undefined}
                  onClick={() => setMobileOpen(false)}
                >
                  <Icon size={19} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </aside>
        <main className="main">
          <header className="topbar">
            <button className="icon-btn mobile-menu" type="button" onClick={() => setMobileOpen((v) => !v)} aria-label="Меню">
              <Menu size={18} />
            </button>
            <div className="page-title">{pageTitleFor(activePath)}</div>
            <div className="top-actions">
              <button className="icon-btn" type="button" onClick={toggleTheme} aria-label="Сменить тему">
                <SunMoon size={18} />
              </button>
              <button className="icon-btn" type="button" aria-label="Уведомления" onClick={() => navigate('/tickets')}>
                <Bell size={18} />
              </button>
              <button
                className="user-chip"
                type="button"
                onClick={() => navigate('/my-data')}
                aria-label="Мои данные"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
              >
                <span>{displayName(user)}</span>
                <div className="avatar">{initials(user)}</div>
              </button>
            </div>
          </header>
          <div className="wrap">{children ?? <Outlet />}</div>
        </main>
      </div>
    </div>
  );
}
